import dayjs from '~/timeInit';

import type {
  AtolCertificateDetail,
  AtolCertificateItems,
  PatchCertificateItemsPayload,
} from '~/services/TravelComplianceService';

// ---------------------------------------------------------------------------
// Item group key — mirrors the keys on GET items and PATCH payload
// ---------------------------------------------------------------------------

export const ITEM_GROUP_KEYS = [
  'flights',
  'offlineFlights',
  'hotels',
  'bedbankHotels',
  'experiences',
  'addons',
  'tours',
  'tourOptionalExperiences',
  'cruises',
  'carHires',
] as const;

export type ItemGroupKey = (typeof ITEM_GROUP_KEYS)[number];

export const ITEM_GROUP_LABELS: Record<ItemGroupKey, string> = {
  flights: 'Flights',
  offlineFlights: 'Offline Flights',
  hotels: 'Hotels',
  bedbankHotels: 'Bedbank Hotels',
  experiences: 'Experiences',
  addons: 'Addons',
  tours: 'Tours',
  tourOptionalExperiences: 'Tour Optional Experiences',
  cruises: 'Cruises',
  carHires: 'Car Hire',
};

export type UpdateType = 'change_dates' | 'change_description' | 'change_pnr' | 'delete';

// ---------------------------------------------------------------------------
// Item type aliases extracted from the contract
// ---------------------------------------------------------------------------

export type AtolFlightItem = AtolCertificateItems['flights'][number];
export type AtolHotelItem = AtolCertificateItems['hotels'][number];
export type AtolExperienceItem = AtolCertificateItems['experiences'][number];
export type AtolTourItem = AtolCertificateItems['tours'][number];
export type AtolTourOptionalExperienceItem = AtolCertificateItems['tourOptionalExperiences'][number];
export type AtolCruiseItem = AtolCertificateItems['cruises'][number];

export type FlightLeg = AtolFlightItem['legs'][number];

// Non-flight items share startDate/endDate
export type ItemWithDates = { startDate?: string; endDate?: string };

// ---------------------------------------------------------------------------
// Editable form state — a deep-cloned snapshot of each item with editability
// ---------------------------------------------------------------------------

export type AnyItem = AtolCertificateItems[ItemGroupKey][number];

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export interface EditableItem {
  groupKey: ItemGroupKey;
  original: AnyItem;
  current: AnyItem;
  deleted: boolean;
}

/** Flatten all item groups into a single EditableItem array for form state. */
export function buildEditableItems(items: AtolCertificateItems): Array<EditableItem> {
  const result: Array<EditableItem> = [];
  for (const groupKey of ITEM_GROUP_KEYS) {
    const group = items[groupKey];
    if (!group) {
      continue;
    }
    for (const item of group) {
      result.push({
        groupKey,
        original: deepClone(item),
        current: deepClone(item),
        deleted: false,
      });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Diffing — detect which update types apply per item
// ---------------------------------------------------------------------------

function datesChanged(groupKey: ItemGroupKey, original: AnyItem, current: AnyItem): boolean {
  if (groupKey === 'flights' || groupKey === 'offlineFlights') {
    const originalFlight = original as AtolFlightItem;
    const currentFlight = current as AtolFlightItem;
    if (originalFlight.legs.length !== currentFlight.legs.length) {
      return true;
    }
    for (let legIndex = 0; legIndex < originalFlight.legs.length; legIndex++) {
      if (originalFlight.legs[legIndex].departingDate !== currentFlight.legs[legIndex].departingDate) {
        return true;
      }
      if (originalFlight.legs[legIndex].arrivingDate !== currentFlight.legs[legIndex].arrivingDate) {
        return true;
      }
    }
    return false;
  }

  const originalDated = original as ItemWithDates;
  const currentDated = current as ItemWithDates;
  return originalDated.startDate !== currentDated.startDate || originalDated.endDate !== currentDated.endDate;
}

function pnrChanged(groupKey: ItemGroupKey, original: AnyItem, current: AnyItem): boolean {
  if (groupKey !== 'flights' && groupKey !== 'offlineFlights') {
    return false;
  }
  const originalFlight = original as AtolFlightItem;
  const currentFlight = current as AtolFlightItem;
  return originalFlight.airlinePnr !== currentFlight.airlinePnr;
}

function descriptionChanged(groupKey: ItemGroupKey, original: AnyItem, current: AnyItem): boolean {
  switch (groupKey) {
    case 'hotels':
    case 'bedbankHotels': {
      const originalHotel = original as AtolHotelItem;
      const currentHotel = current as AtolHotelItem;
      if (originalHotel.hotelName !== currentHotel.hotelName) {
        return true;
      }
      for (
        let roomIndex = 0;
        roomIndex < Math.max(originalHotel.rooms.length, currentHotel.rooms.length);
        roomIndex++
      ) {
        if (originalHotel.rooms[roomIndex]?.roomName !== currentHotel.rooms[roomIndex]?.roomName) {
          return true;
        }
      }
      return false;
    }
    case 'experiences':
    case 'addons':
      return (original as AtolExperienceItem).description !== (current as AtolExperienceItem).description;
    case 'tours':
      return (original as AtolTourItem).tourName !== (current as AtolTourItem).tourName;
    case 'tourOptionalExperiences':
      return (original as AtolTourOptionalExperienceItem).name !== (current as AtolTourOptionalExperienceItem).name;
    case 'cruises':
      return (original as AtolCruiseItem).cruiseName !== (current as AtolCruiseItem).cruiseName;
    default:
      return false;
  }
}

export function getUpdateTypes(editableItem: EditableItem): Array<UpdateType> {
  if (editableItem.deleted) {
    return ['delete'];
  }
  const types: Array<UpdateType> = [];
  if (datesChanged(editableItem.groupKey, editableItem.original, editableItem.current)) {
    types.push('change_dates');
  }
  if (descriptionChanged(editableItem.groupKey, editableItem.original, editableItem.current)) {
    types.push('change_description');
  }
  if (pnrChanged(editableItem.groupKey, editableItem.original, editableItem.current)) {
    types.push('change_pnr');
  }
  return types;
}

export function hasChanges(editableItem: EditableItem): boolean {
  return editableItem.deleted || getUpdateTypes(editableItem).length > 0;
}

// ---------------------------------------------------------------------------
// Build PATCH payload from changed items only
// ---------------------------------------------------------------------------

type PayloadGroupEntry = { item: AnyItem; updateTypes: Array<UpdateType> };
type PayloadGroups = { [K in ItemGroupKey]?: Array<PayloadGroupEntry> };

export function buildPatchPayload(
  certificateId: string,
  editableItems: Array<EditableItem>,
): PatchCertificateItemsPayload | null {
  const changedItems = editableItems.filter(hasChanges);
  if (changedItems.length === 0) {
    return null;
  }

  const payload: PatchCertificateItemsPayload = { certificateId };
  const payloadGroups = payload as PayloadGroups;

  for (const changedItem of changedItems) {
    const updateTypes = getUpdateTypes(changedItem);
    if (updateTypes.length === 0) {
      continue;
    }

    const { groupKey } = changedItem;
    const existingGroup = payloadGroups[groupKey];
    if (existingGroup) {
      existingGroup.push({ item: changedItem.current, updateTypes });
    } else {
      payloadGroups[groupKey] = [{ item: changedItem.current, updateTypes }];
    }
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Helpers for editable/display-only field classification
// ---------------------------------------------------------------------------

export function isDescriptionEditable(groupKey: ItemGroupKey): boolean {
  return ['hotels', 'bedbankHotels', 'experiences', 'addons', 'tours', 'tourOptionalExperiences', 'cruises'].includes(
    groupKey,
  );
}

export function isDateEditable(_groupKey: ItemGroupKey): boolean {
  return true;
}

export function getDescriptionLabel(groupKey: ItemGroupKey): string {
  switch (groupKey) {
    case 'hotels':
    case 'bedbankHotels':
      return 'Hotel Name';
    case 'experiences':
    case 'addons':
      return 'Description';
    case 'tours':
      return 'Tour Name';
    case 'tourOptionalExperiences':
      return 'Experience Name';
    case 'cruises':
      return 'Cruise Name';
    default:
      return 'Name';
  }
}

export function getDescriptionValue(groupKey: ItemGroupKey, item: AnyItem): string {
  switch (groupKey) {
    case 'hotels':
    case 'bedbankHotels':
      return (item as AtolHotelItem).hotelName ?? '';
    case 'experiences':
    case 'addons':
      return (item as AtolExperienceItem).description ?? '';
    case 'tours':
      return (item as AtolTourItem).tourName ?? '';
    case 'tourOptionalExperiences':
      return (item as AtolTourOptionalExperienceItem).name ?? '';
    case 'cruises':
      return (item as AtolCruiseItem).cruiseName ?? '';
    default:
      return '';
  }
}

export function setDescriptionValue(groupKey: ItemGroupKey, item: AnyItem, value: string): AnyItem {
  const clone = deepClone(item);
  switch (groupKey) {
    case 'hotels':
    case 'bedbankHotels':
      (clone as AtolHotelItem).hotelName = value;
      break;
    case 'experiences':
    case 'addons':
      (clone as AtolExperienceItem).description = value;
      break;
    case 'tours':
      (clone as AtolTourItem).tourName = value;
      break;
    case 'tourOptionalExperiences':
      (clone as AtolTourOptionalExperienceItem).name = value;
      break;
    case 'cruises':
      (clone as AtolCruiseItem).cruiseName = value;
      break;
  }
  return clone;
}

export function getReferenceLabel(groupKey: ItemGroupKey): string {
  if (groupKey === 'flights' || groupKey === 'offlineFlights') {
    return 'PNR';
  }
  return 'Booking Ref';
}

export function getReferenceValue(groupKey: ItemGroupKey, item: AnyItem): string {
  if (groupKey === 'flights' || groupKey === 'offlineFlights') {
    const flightItem = item as AtolFlightItem;
    return flightItem.providerPnr ?? flightItem.bookingRef ?? '';
  }
  return item.bookingRef ?? '';
}

// ---------------------------------------------------------------------------
// Traveller editing
// ---------------------------------------------------------------------------

export type EditableTraveller = AtolCertificateDetail['travellers'][number];

export interface TravellerEditState {
  original: Array<EditableTraveller>;
  current: Array<EditableTraveller>;
}

export function buildTravellerEditState(travellers: Array<EditableTraveller>): TravellerEditState {
  return {
    original: deepClone(travellers),
    current: deepClone(travellers),
  };
}

export function haveTravellersChanged(state: TravellerEditState): boolean {
  for (let index = 0; index < state.original.length; index++) {
    const originalTraveller = state.original[index];
    const currentTraveller = state.current[index];
    if (!originalTraveller || !currentTraveller) {
      return true;
    }
    if (
      originalTraveller.givenName !== currentTraveller.givenName ||
      originalTraveller.surname !== currentTraveller.surname
    ) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

/** Count how many items have pending changes across all groups. */
export function countChanges(editableItems: Array<EditableItem>, travellerState?: TravellerEditState): number {
  let count = editableItems.filter(hasChanges).length;
  if (travellerState && haveTravellersChanged(travellerState)) {
    count += 1;
  }
  return count;
}

// ---------------------------------------------------------------------------
// Date validation
// ---------------------------------------------------------------------------

export interface DateValidationError {
  itemId: string;
  field: string;
  message: string;
}

const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';

/**
 * Validate chronological consistency of dates for all non-deleted editable items.
 *
 * - Items with startDate/endDate: start must not be after end.
 * - Flight legs: departing must not be after arriving.
 *
 * Returns an array of errors (empty = valid).
 */
export function validateDates(editableItems: Array<EditableItem>): Array<DateValidationError> {
  const errors: Array<DateValidationError> = [];

  for (const editItem of editableItems) {
    if (editItem.deleted) {
      continue;
    }

    if (editItem.groupKey === 'flights' || editItem.groupKey === 'offlineFlights') {
      const flightItem = editItem.current as AtolFlightItem;
      for (const leg of flightItem.legs) {
        if (leg.departingDate && leg.arrivingDate && leg.departingDate > leg.arrivingDate) {
          const arrivingFormatted = dayjs(leg.arrivingDate).format(DATE_DISPLAY_FORMAT);
          const departingFormatted = dayjs(leg.departingDate).format(DATE_DISPLAY_FORMAT);
          errors.push({
            itemId: flightItem.id,
            field: `leg-${leg.legId}-arrivingDate`,
            message: `Arriving date (${arrivingFormatted}) must not be before departing date (${departingFormatted}).`,
          });
        }
      }
    } else {
      const datedItem = editItem.current as ItemWithDates;
      if (datedItem.startDate && datedItem.endDate && datedItem.startDate > datedItem.endDate) {
        const endFormatted = dayjs(datedItem.endDate).format(DATE_DISPLAY_FORMAT);
        const startFormatted = dayjs(datedItem.startDate).format(DATE_DISPLAY_FORMAT);
        errors.push({
          itemId: editItem.current.id,
          field: 'endDate',
          message: `End date (${endFormatted}) must not be before start date (${startFormatted}).`,
        });
      }
    }
  }

  return errors;
}

/** Check whether a specific item+field has a validation error. */
export function getDateError(errors: Array<DateValidationError>, itemId: string, field: string): string | undefined {
  return errors.find((validationError) => validationError.itemId === itemId && validationError.field === field)
    ?.message;
}

// ---------------------------------------------------------------------------
// Build PATCH payload (with travellers)
// ---------------------------------------------------------------------------

export function buildPatchPayloadWithTravellers(
  certificateId: string,
  editableItems: Array<EditableItem>,
  travellerState?: TravellerEditState,
): PatchCertificateItemsPayload | null {
  const changedItems = editableItems.filter(hasChanges);
  const travellersChanged = travellerState ? haveTravellersChanged(travellerState) : false;

  if (changedItems.length === 0 && !travellersChanged) {
    return null;
  }

  const payload: PatchCertificateItemsPayload = { certificateId };

  if (travellersChanged && travellerState) {
    payload.travellers = travellerState.current;
  }

  const payloadGroups = payload as PayloadGroups;
  for (const changedItem of changedItems) {
    const updateTypes = getUpdateTypes(changedItem);
    if (updateTypes.length === 0) {
      continue;
    }

    const { groupKey } = changedItem;
    const existingGroup = payloadGroups[groupKey];
    if (existingGroup) {
      existingGroup.push({ item: changedItem.current, updateTypes });
    } else {
      payloadGroups[groupKey] = [{ item: changedItem.current, updateTypes }];
    }
  }

  return payload;
}
